import React from 'react';
import '../css/common.css';
import '../App.css';
import './Dashboard.css';
import {strings} from "../languages/localizationStrings";
import {Cookies} from 'react-cookie';
import ProgressTracking from '../tools/progressTracking';
import IrisMascot from '../components/IrisMascot';
import { getIrisDashboardMessage, getIrisMood } from '../tools/irisPersonality';
import { getAvatar, getNickname } from '../tools/localStorage';
import { auth, isFirebaseConfigured } from '../tools/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import BadgeArt from '../components/BadgeArt';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import iconImg from '../icon.png';

class Dashboard extends React.Component {
    constructor(props) {
        super(props);
        this.cookies = new Cookies();
        let language = strings.getLanguage();

        const stats = ProgressTracking.getStats();

        const now = new Date();
        this.state = {
            language: language,
            stats: stats,
            user: auth ? auth.currentUser : null,
            nickname: getNickname(),
            avatar: getAvatar(),
            calendarYear: now.getFullYear(),
            calendarMonth: now.getMonth(),
            animated: false,
        }
        this.changeLanguage = this.changeLanguage.bind(this);
        this._counterRefs = {};
    }

    changeLanguage(lang) {
        strings.setLanguage(lang);
        this.cookies.set('lang', lang, {path: '/'});
        this.setState({
            language: lang
        });
    }

    componentDidMount() {
        const stats = ProgressTracking.getStats();
        this.setState({ stats });

        if (isFirebaseConfigured && auth) {
            this._unsubAuth = onAuthStateChanged(auth, (user) => {
                this.setState({ user, nickname: getNickname(), avatar: getAvatar() });
            });
        }

        // Trigger entrance animations after mount
        requestAnimationFrame(() => {
            this.setState({ animated: true });
            this._animateCounters();
        });
    }

    componentWillUnmount() {
        if (this._unsubAuth) this._unsubAuth();
        if (this._animFrame) cancelAnimationFrame(this._animFrame);
    }

    _animateCounters() {
        const targets = {
            streak: this.state.stats.currentStreak,
            record: this.state.stats.longestStreak,
            sessions: this.state.stats.totalSessions,
            points: this.state.stats.totalPoints,
        };

        const duration = 1000;
        const start = performance.now();

        const tick = (now) => {
            const elapsed = now - start;
            const t = Math.min(elapsed / duration, 1);
            // Ease-out cubic
            const ease = 1 - Math.pow(1 - t, 3);

            Object.entries(targets).forEach(([key, target]) => {
                const el = this._counterRefs[key];
                if (el) {
                    el.textContent = Math.round(ease * target);
                }
            });

            if (t < 1) {
                this._animFrame = requestAnimationFrame(tick);
            }
        };
        this._animFrame = requestAnimationFrame(tick);
    }

    navigateMonth(direction) {
        this.setState(prevState => {
            let newMonth = prevState.calendarMonth + direction;
            let newYear = prevState.calendarYear;
            if (newMonth < 0) { newMonth = 11; newYear--; }
            if (newMonth > 11) { newMonth = 0; newYear++; }
            return { calendarMonth: newMonth, calendarYear: newYear };
        });
    }

    isCurrentMonth() {
        const now = new Date();
        return this.state.calendarYear === now.getFullYear() && this.state.calendarMonth === now.getMonth();
    }

    renderHero() {
        const { stats } = this.state;
        const hasCompletedToday = stats.lastCompletedDate === ProgressTracking.getTodayDate();
        const ringRadius = 72;
        const circumference = 2 * Math.PI * ringRadius;
        const progress = hasCompletedToday ? 1 : 0;
        const dashOffset = circumference * (1 - progress);

        return (
            <div className={`dash-hero ${this.state.animated ? 'dash-hero--visible' : ''}`}>
                <div className="dash-hero__ring-wrap">
                    <svg className="dash-hero__ring" viewBox="0 0 160 160">
                        <circle
                            cx="80" cy="80" r={ringRadius}
                            fill="none"
                            stroke="var(--dash-ring-track)"
                            strokeWidth="8"
                        />
                        <circle
                            cx="80" cy="80" r={ringRadius}
                            fill="none"
                            stroke="var(--dash-ring-fill)"
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={this.state.animated ? dashOffset : circumference}
                            className="dash-hero__ring-progress"
                            transform="rotate(-90 80 80)"
                        />
                    </svg>
                    <div className="dash-hero__mascot">
                        <IrisMascot
                            size="small"
                            mood={getIrisMood(stats)}
                        />
                    </div>
                </div>

                {stats.currentStreak > 0 && (
                    <div className="dash-hero__streak">
                        <span className="dash-hero__flame">🔥</span>
                        <span className="dash-hero__streak-num">{stats.currentStreak}</span>
                        <span className="dash-hero__streak-label">{strings.currentStreak}</span>
                    </div>
                )}

                <p className="dash-hero__message">
                    {getIrisDashboardMessage(stats)}
                </p>

                {hasCompletedToday && (
                    <span className="dash-hero__done-badge">{strings.greatJobToday || 'Great job today!'}</span>
                )}
            </div>
        );
    }

    renderStats() {
        const { stats, animated } = this.state;
        const cards = [
            { key: 'streak',   icon: '🔥', value: stats.currentStreak, label: strings.currentStreak, sub: strings.days, accent: 'streak' },
            { key: 'record',   icon: '⭐', value: stats.longestStreak, label: strings.longestStreak, sub: strings.days, accent: 'record' },
            { key: 'sessions', icon: '💪', value: stats.totalSessions, label: strings.totalSessions, sub: `${strings.completedDays}: ${stats.completedDays}`, accent: 'sessions' },
            { key: 'points',   icon: '🏆', value: stats.totalPoints,   label: strings.totalPoints,   sub: `${stats.badges.length} ${strings.badges}`, accent: 'points' },
        ];

        return (
            <div className="stats-grid">
                {cards.map((card, i) => (
                    <div
                        key={card.key}
                        className={`stat-card stat-card--${card.accent} ${animated ? 'stat-card--visible' : ''}`}
                        style={{ animationDelay: `${i * 80}ms` }}
                    >
                        <div className="stat-icon">{card.icon}</div>
                        <div
                            className="stat-value"
                            ref={el => { this._counterRefs[card.key] = el; }}
                        >
                            0
                        </div>
                        <div className="stat-label">{card.label}</div>
                        <div className="stat-sublabel">{card.sub}</div>
                    </div>
                ))}
            </div>
        );
    }

    renderWeeklyChart() {
        const weekData = ProgressTracking.getWeekData();
        const maxCount = Math.max(1, ...weekData.map(d => d.count));

        return (
            <div className={`dash-week ${this.state.animated ? 'dash-week--visible' : ''}`}>
                <h2 className="section-title">{strings.thisWeek || 'This Week'}</h2>
                <div className="dash-week__chart">
                    {weekData.map((day, i) => {
                        const heightPct = day.count > 0 ? Math.max(20, (day.count / maxCount) * 100) : 0;
                        const barClass = [
                            'dash-week__bar',
                            day.isToday ? 'dash-week__bar--today' : '',
                            day.isFuture ? 'dash-week__bar--future' : '',
                            day.count === 0 && !day.isFuture ? 'dash-week__bar--empty' : '',
                        ].filter(Boolean).join(' ');

                        return (
                            <div key={i} className="dash-week__col">
                                <div className="dash-week__bar-track">
                                    {day.count > 0 && (
                                        <div
                                            className={barClass}
                                            style={{
                                                height: this.state.animated ? `${heightPct}%` : '0%',
                                                transitionDelay: `${i * 60 + 300}ms`
                                            }}
                                        >
                                            <span className="dash-week__bar-count">{day.count}</span>
                                        </div>
                                    )}
                                    {day.count === 0 && !day.isFuture && (
                                        <div className={barClass} />
                                    )}
                                    {day.isFuture && (
                                        <div className={barClass} />
                                    )}
                                </div>
                                <span className={`dash-week__day-label ${day.isToday ? 'dash-week__day-label--today' : ''}`}>
                                    {day.dayLabel}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    renderCalendar() {
        const { calendarYear: year, calendarMonth: month } = this.state;

        const progress = ProgressTracking.getProgress();
        const activityMap = {};
        Object.entries(progress.completedDays).forEach(([date, dayData]) => {
            activityMap[date] = {
                date,
                completed: true,
                count: dayData.count,
                points: dayData.points,
                level: ProgressTracking.getActivityLevel(dayData),
            };
        });

        const today = ProgressTracking.getTodayDate();

        const firstDay = new Date(year, month, 1);
        const totalDays = new Date(year, month + 1, 0).getDate();

        const startOffset = (firstDay.getDay() + 6) % 7;

        const monthName = firstDay.toLocaleString(this.state.language, { month: 'long' });
        const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

        const cells = [];
        for (let i = 0; i < startOffset; i++) cells.push(null);
        for (let d = 1; d <= totalDays; d++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const dayData = activityMap[dateStr];
            const isToday = dateStr === today;
            const isFuture = dateStr > today;
            cells.push({ d, dateStr, dayData, isToday, isFuture });
        }
        while (cells.length % 7 !== 0) cells.push(null);

        const weeks = [];
        for (let i = 0; i < cells.length; i += 7) {
            weeks.push(cells.slice(i, i + 7));
        }

        const canGoPrev = progress.firstSessionDate
            ? (() => {
                const first = new Date(progress.firstSessionDate);
                return year > first.getFullYear() || (year === first.getFullYear() && month > first.getMonth());
              })()
            : false;
        const canGoNext = !this.isCurrentMonth();

        return (
            <div className="calendar-grid">
                <div className="calendar-month-header">
                    <button
                        className="calendar-nav-btn"
                        onClick={() => this.navigateMonth(-1)}
                        disabled={!canGoPrev}
                        aria-label={strings.previousMonth || 'Previous month'}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span className="calendar-month-title">{monthName} {year}</span>
                    <button
                        className="calendar-nav-btn"
                        onClick={() => this.navigateMonth(1)}
                        disabled={!canGoNext}
                        aria-label={strings.nextMonth || 'Next month'}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>

                <div className="calendar-day-labels">
                    {dayLabels.map((label, i) => (
                        <div key={i} className="calendar-day-label">{label}</div>
                    ))}
                </div>

                <div className="calendar-weeks">
                    {weeks.map((week, wi) => (
                        <div key={wi} className="calendar-week">
                            {week.map((cell, di) => {
                                if (!cell) {
                                    return <div key={di} className="calendar-day empty"></div>;
                                }
                                const { d, dayData, isToday, isFuture } = cell;
                                const level = (!isFuture && dayData) ? dayData.level : 0;
                                const classes = [
                                    'calendar-day',
                                    isFuture ? 'future' : `level-${level}`,
                                    isToday ? 'today' : ''
                                ].filter(Boolean).join(' ');

                                const starIcon = !isFuture && dayData
                                    ? (dayData.level >= 4 ? '🌟' : '⭐')
                                    : null;

                                const tooltip = dayData
                                    ? `${cell.dateStr}: ${dayData.count} session${dayData.count !== 1 ? 's' : ''} · ${dayData.points} pts`
                                    : cell.dateStr;

                                return (
                                    <div key={di} className={classes} title={tooltip}>
                                        <span className="calendar-day-number">{d}</span>
                                        {starIcon && <span className="calendar-day-star">{starIcon}</span>}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                <div className="calendar-legend">
                    <div className="legend-item level-0"></div>
                    <span className="legend-item-text">0</span>
                    <div className="legend-item level-2"></div>
                    <span className="legend-item-text">1</span>
                    <div className="legend-item level-3"></div>
                    <span className="legend-item-text">2</span>
                    <div className="legend-item level-4"></div>
                    <span className="legend-item-text">3+</span>
                </div>
            </div>
        );
    }

    renderNextGoal() {
        const goal = ProgressTracking.getNextGoal();
        if (!goal) return null;

        let message = '';
        let icon = '';
        const badgeKey = goal.type === 'streak'
            ? `badge_${goal.target}_day_streak`
            : goal.type === 'sessions'
            ? `badge_${goal.target}_sessions`
            : `badge_${goal.target}_points`;
        const badgeName = strings[badgeKey] || '';

        if (goal.type === 'streak') {
            icon = '🔥';
            message = strings.formatString
                ? strings.formatString(strings.moreDaysStreak || '{0} more days for a {1}-Day Streak!', goal.remaining, goal.target)
                : `${goal.remaining} more days for a ${goal.target}-Day Streak!`;
        } else if (goal.type === 'sessions') {
            icon = '💪';
            message = strings.formatString
                ? strings.formatString(strings.moreSessions || "{0} more sessions to earn '{1}'!", goal.remaining, badgeName)
                : `${goal.remaining} more sessions to earn '${badgeName}'!`;
        } else {
            icon = '🏆';
            message = strings.formatString
                ? strings.formatString(strings.morePoints || "{0} more points to '{1}'!", goal.remaining, badgeName)
                : `${goal.remaining} more points to '${badgeName}'!`;
        }

        const progressPct = Math.min(goal.progress * 100, 100);

        return (
            <div className={`dash-next-goal ${this.state.animated ? 'dash-next-goal--visible' : ''}`}>
                <div className="dash-next-goal__header">
                    <span className="dash-next-goal__icon">{icon}</span>
                    <h2 className="section-title">{strings.nextGoal || 'Next Goal'}</h2>
                </div>
                <p className="dash-next-goal__text">{message}</p>
                <div className="dash-next-goal__bar-track">
                    <div
                        className="dash-next-goal__bar-fill"
                        style={{ width: this.state.animated ? `${progressPct}%` : '0%' }}
                    />
                </div>
            </div>
        );
    }

    getLocalizedBadgeName(badge) {
        const key = `badge_${badge.id}`;
        return strings[key] || badge.name;
    }

    renderXpLevel() {
        const { stats } = this.state;
        // XP level system: each level requires more points
        // Level thresholds: 0, 50, 150, 350, 700, 1200, 2000, 3200, 5000, 8000 ...
        const thresholds = [0, 50, 150, 350, 700, 1200, 2000, 3200, 5000, 8000, 12000];
        let level = 1;
        let currentThreshold = 0;
        let nextThreshold = 50;
        for (let i = 1; i < thresholds.length; i++) {
            if (stats.totalPoints >= thresholds[i]) {
                level = i + 1;
                currentThreshold = thresholds[i];
                nextThreshold = thresholds[i + 1] || thresholds[i] * 2;
            } else {
                nextThreshold = thresholds[i];
                break;
            }
        }
        const xpInLevel = stats.totalPoints - currentThreshold;
        const xpNeeded = nextThreshold - currentThreshold;
        const progressPct = Math.min((xpInLevel / xpNeeded) * 100, 100);

        return (
            <div className="dash-xp-level">
                <div className="dash-xp-level__badge">{level}</div>
                <div className="dash-xp-level__info">
                    <div className="dash-xp-level__label">
                        {strings.level || 'Level'} {level}
                    </div>
                    <div className="dash-xp-level__bar-track">
                        <div
                            className="dash-xp-level__bar-fill"
                            style={{ width: this.state.animated ? `${progressPct}%` : '0%' }}
                        />
                    </div>
                    <div className="dash-xp-level__xp">
                        {xpInLevel} / {xpNeeded} XP
                    </div>
                </div>
            </div>
        );
    }

    renderBadges() {
        const { badges } = this.state.stats;
        const allBadges = ProgressTracking.getAllBadges();
        const earnedIds = new Set(badges.map(b => b.id));
        const earnedMap = {};
        badges.forEach(b => { earnedMap[b.id] = b; });

        // Consider badges earned within 7 days as "new"
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        const totalBadges = allBadges.length;
        const earnedCount = badges.length;

        // Group badges by category for organized display
        const categories = [
            { key: 'milestone', label: strings.badgeCatMilestone || 'Milestone', ids: ['first_session'] },
            { key: 'streak', label: strings.badgeCatStreak || 'Streaks', ids: ['3_day_streak','7_day_streak','14_day_streak','30_day_streak','60_day_streak','365_day_streak'] },
            { key: 'sessions', label: strings.badgeCatSessions || 'Sessions', ids: ['10_sessions','25_sessions','50_sessions','100_sessions','200_sessions'] },
            { key: 'points', label: strings.badgeCatPoints || 'Points', ids: ['100_points','1000_points','5000_points'] },
            { key: 'special', label: strings.badgeCatSpecial || 'Special', ids: ['explorer','iron_eyes','pattern_pro','level_up','overachiever','hat_trick','comeback_kid','weekend_warrior'] },
            { key: 'vision', label: strings.badgeCatVision || 'Vision', ids: ['eye_curious','progress_watcher','perfect_vision'] },
        ];

        const badgeMap = {};
        allBadges.forEach(b => { badgeMap[b.id] = b; });

        let shimmerIdx = 0;

        return (
            <div className="section">
                <div className="dash-badges-header">
                    <h2 className="section-title">{strings.badges}</h2>
                    <span className="dash-badges-count">
                        {strings.formatString
                            ? strings.formatString(strings.badgesUnlocked || '{0} of {1} unlocked', earnedCount, totalBadges)
                            : `${earnedCount} / ${totalBadges}`
                        }
                    </span>
                </div>
                <div className="dash-badges-progress-track">
                    <div
                        className="dash-badges-progress-fill"
                        style={{ width: this.state.animated ? `${(earnedCount / totalBadges) * 100}%` : '0%' }}
                    />
                </div>

                {categories.map((cat) => (
                    <div key={cat.key}>
                        <div className="badge-category-label">{cat.label}</div>
                        <div className="badges-grid">
                            {cat.ids.map((id) => {
                                const badgeDef = badgeMap[id];
                                if (!badgeDef) return null;
                                const earned = earnedIds.has(id);
                                const earnedBadge = earnedMap[id];
                                const localName = earned
                                    ? this.getLocalizedBadgeName(earnedBadge)
                                    : (strings[`badge_${id}`] || id);
                                const isNew = earned && earnedBadge.date && new Date(earnedBadge.date) > sevenDaysAgo;
                                const earnedDate = earned && earnedBadge.date
                                    ? new Date(earnedBadge.date).toLocaleDateString()
                                    : '';

                                // Stagger shimmer animation per earned badge
                                const delay = earned ? `${(shimmerIdx++) * 0.7}s` : '0s';

                                return (
                                    <div
                                        key={id}
                                        className={`badge-item ${earned ? 'badge-item--earned' : 'badge-item--locked'} ${isNew ? 'badge-item--new' : ''}`}
                                        title={earned ? `${localName}${earnedDate ? ' — ' + earnedDate : ''}` : `${localName} (${strings.locked || 'Locked'})`}
                                        style={earned ? { '--shimmer-delay': delay } : undefined}
                                    >
                                        <div className="badge-icon">
                                            <BadgeArt badgeId={id} emoji={badgeDef.icon} locked={!earned} />
                                        </div>
                                        <div className="badge-name">{localName}</div>
                                        {isNew && <span className="badge-new-tag">NEW</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    renderCta() {
        const { stats } = this.state;
        const hasCompletedToday = stats.lastCompletedDate === ProgressTracking.getTodayDate();
        const yesterday = ProgressTracking.getDateDaysAgo(1);
        const didYesterday = stats.lastCompletedDate === yesterday;
        const streakAtRisk = didYesterday && !hasCompletedToday && stats.currentStreak > 0;

        let ctaClass = 'cta-button';
        let ctaText = strings.startTraining;
        let ctaSub = null;

        if (hasCompletedToday) {
            ctaClass += ' cta-button--done';
            ctaText = strings.continueTraining;
            ctaSub = strings.todayDone || 'Done for today';
        } else if (streakAtRisk) {
            ctaClass += ' cta-button--warning';
            ctaText = strings.startTraining;
            ctaSub = strings.dontBreakStreak || "Don't break your streak!";
        } else {
            ctaClass += ' cta-button--pulse';
        }

        return (
            <div className="cta-section">
                {ctaSub && <p className="cta-sub">{ctaSub}</p>}
                <a href="/welcome" className={ctaClass}>
                    {hasCompletedToday && <span className="cta-check">✓ </span>}
                    {ctaText}
                </a>
            </div>
        );
    }

    render() {
        const AVATAR_MAP = {
            dog:'🐶', cat:'🐱', owl:'🦉', moose:'🦌', classic:'👁️',
            robot:'🤖', alien:'👽', unicorn:'🦄', penguin:'🐧', panda:'🐼', frog:'🐸', fox:'🦊',
        };
        const avatarEmoji = AVATAR_MAP[this.state.avatar] || '👁️';

        return (
            <div className="App dashboard-container">
                {/* Top navigation bar */}
                <div className="top-nav">
                    <div className="nav-brand">
                        <img src={iconImg} alt="" className="nav-logo" />
                        <span>{strings.appName}</span>
                    </div>
                    <div className="nav-controls">
                        <a href="/welcome" className="nav-icon-btn" title={strings.startTraining} aria-label={strings.startTraining}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </a>
                    </div>
                </div>

                {/* User profile header */}
                <div className="dash-profile">
                    <span className="dash-avatar">{avatarEmoji}</span>
                    <div className="dash-profile-info">
                        <h1 className="pageTitle">
                            {this.state.nickname
                                ? `${this.state.nickname}'s ${strings.dashboard}`
                                : strings.dashboard}
                        </h1>
                        {this.state.user && (
                            <span className="dash-sync-badge">&#x2601; {strings.synced}</span>
                        )}
                    </div>
                </div>

                {/* Hero: Progress Ring + IRIS + Streak */}
                {this.renderHero()}

                {/* XP Level */}
                {this.renderXpLevel()}

                {/* Stats Summary Cards */}
                {this.renderStats()}

                {/* Weekly Mini Chart */}
                {this.renderWeeklyChart()}

                {/* Next Goal */}
                {this.renderNextGoal()}

                {/* Activity Calendar */}
                <div className="section">
                    <h2 className="section-title">{strings.activityCalendar}</h2>
                    {this.renderCalendar()}
                </div>

                {/* Badges Section */}
                {this.renderBadges()}

                {/* Call to Action */}
                {this.renderCta()}

                {/* Quebec Badge */}
                <div className={"quebec-badge"}>
                    <p className={"textGray"}><small>{strings.builtInQuebec}</small></p>
                    <p className={"textGray"}><small><i>{strings.peopleDriven}</i></small></p>
                </div>
            </div>
        );
    }
}

export default Dashboard;
